package com.klantenstop.web.utils

import com.klantenstop.web.bindings.PostHogJs
import com.materialkobweb.utils.Logger

/**
 * Central analytics manager. All PostHog calls in the app go through here.
 *
 * Design goals:
 * - Single point of control for enabling/disabling tracking (e.g. GDPR consent)
 * - Cookieless mode by default — page views and events fire from first load with no browser storage
 * - Full cookie mode (session replay, identity) only after explicit Klaro consent
 * - Typed Kotlin API instead of raw JS interop scattered across the codebase
 *
 * Initialise once at app startup via [initialize]. After that, callers only need:
 *   AnalyticsManager.trackEvent(Events.SEARCH_PERFORMED, mapOf("query" to q))
 *   AnalyticsManager.trackPageView("/zoeken")
 */
object AnalyticsManager {

    // -------------------------------------------------------------------------
    // Event name constants — use these everywhere to avoid typos and keep the
    // analytics.md table in sync with the code.
    // -------------------------------------------------------------------------
    object Events {
        const val SEARCH_PERFORMED = "search_performed"
        const val SEARCH_PAGE_CHANGED = "search_page_changed"
        const val COMPANY_VIEWED = "company_viewed"
        const val REVIEW_SUBMITTED = "review_submitted"
        const val REVIEW_UPDATED = "review_updated"
        const val COMPANY_CONTACT_CLICKED = "company_contact_clicked"
        const val LOGIN_COMPLETED = "login_completed"
        const val REGISTER_COMPLETED = "register_completed"
        const val LOGOUT = "logout"
        const val CATEGORY_REQUEST_CTA_CLICKED = "category_request_cta_clicked"
        const val AD_SLOT_RENDER_REQUESTED = "ad_slot_render_requested"
        const val AD_SLOT_RENDER_SKIPPED = "ad_slot_render_skipped"
        const val AD_SLOT_RENDER_FAILED = "ad_slot_render_failed"
    }

    // -------------------------------------------------------------------------
    // Internal state
    // -------------------------------------------------------------------------

    /** True once [initialize] has been called with a non-blank API key. */
    private var initialized: Boolean = false

    /**
     * Consent value received before [initialize] completed.
     * Applied immediately after PostHog init so a returning visitor who already accepted
     * is not left stuck in cookieless mode until the next consent change.
     */
    private var pendingConsent: Boolean? = null

    // -------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------

    /**
     * Initialise PostHog.  Called once from AppEntry.
     *
     * When [apiKey] is blank (local dev), initialisation is skipped entirely —
     * all subsequent calls become silent no-ops.
     *
     * After initialisation, consent changes must be forwarded by the caller via [setConsent].
     * AppEntry owns the single [KlaroConsentManager.onConsentChanged] listener and calls
     * both [setConsent] and [identifyUser] from there.
     *
     * **Consent / storage strategy** (`cookieless_mode: "on_reject"`, three-state model):
     *
     * | State | PostHog mode | Browser storage |
     * |---|---|---|
     * | Pre-consent | Cookieless hash | None |
     * | Declined | Cookieless hash | None |
     * | Accepted | Full cookie mode | localStorage + cookie |
     *
     * PostHog's cookieless hash is computed server-side from `ip + ua + domain + daily_salt`.
     * The salt is rotated and deleted after 24 hours. Neither IP nor UA is stored.
     * This satisfies ePrivacy Directive Art. 5(3) — no device storage is written.
     *
     * After [PostHogJs.init], we call [PostHogJs.optOutCapturing] to activate cookieless mode.
     * With `on_reject`, `optOutCapturing()` puts PostHog into cookieless hash mode rather than
     * fully disabling it. If [setConsent] was already called before init completed (e.g. returning
     * visitor whose Klaro cookie was read immediately on load), the buffered consent is applied
     * right after `initialized = true` so PostHog starts in the correct mode from the first event.
     *
     * Other options:
     *  - `mask_all_inputs = true`   → form inputs never recorded in session replay
     *  - `capture_pageview = false` → page views fired manually per SPA route
     *  - `before_send`              → strips PII (email, phone) from all event properties
     */
    fun initialize(apiKey: String, host: String) {
        if (initialized) return

        if (apiKey.isBlank()) {
            Logger.info("AnalyticsManager", "API key not set — tracking disabled")
            return
        }

        try {
            val options = js("{}")
            options["api_host"] = host
            options["cookieless_mode"] = "on_reject"              // cookieless hash when declined; full cookies when opted in
            options["mask_all_inputs"] = true                      // GDPR: hide form inputs in replays
            options["capture_pageview"] = false                    // we fire pageviews manually per SPA route
            options["capture_pageleave"] = true
            options["before_send"] = beforeSend()                 // GDPR: redact PII (email, phone) from all event properties
            if (SiteGlobals.environment != "production") {
                options["debug"] = true                            // verbose PostHog logging in dev/staging
            }

            PostHogJs.init(apiKey, options)

            // Start in cookieless mode. If the user already accepted consent before init
            // completed (returning visitor), pendingConsent will be true and we upgrade
            // to full cookie mode immediately instead of waiting for the next consent change.
            PostHogJs.optOutCapturing()

            initialized = true

            pendingConsent?.let { pending ->
                pendingConsent = null
                setConsent(pending)
            }

            Logger.info("AnalyticsManager", "PostHog initialised (host=$host, cookieless until consent)")
        } catch (e: Throwable) {
            Logger.error("AnalyticsManager", "Failed to initialise PostHog:", e)
        }
    }

    // -------------------------------------------------------------------------
    // Consent (GDPR)
    // -------------------------------------------------------------------------

    /**
     * Grant or revoke analytics consent.
     *
     * With `cookieless_mode: "on_reject"`:
     * - [granted] = true  → [PostHogJs.optInCapturing] switches to full cookie mode
     * - [granted] = false → [PostHogJs.optOutCapturing] activates cookieless hash mode
     *   (NOT full capture-off — with `on_reject`, this is the cookieless state)
     *
     * When revoking previously-granted consent, [PostHogJs.reset] is called first to clear
     * any stored user identity before dropping back to cookieless mode.
     */
    fun setConsent(granted: Boolean) {
        if (!initialized) {
            // Buffer the consent value — will be applied once initialize() completes.
            pendingConsent = granted
            return
        }
        val wasGranted = PostHogJs.hasOptedInCapturing()
        if (granted == wasGranted) return
        try {
            if (granted) {
                PostHogJs.optInCapturing()
            } else {
                if (wasGranted) {
                    // Clear stored identity before reverting to cookieless — avoids linking
                    // future cookieless events to the previously identified user profile.
                    PostHogJs.reset()
                }
                PostHogJs.optOutCapturing()
                Logger.info("AnalyticsManager", "Consent revoked — switched to cookieless hash mode")
            }
        } catch (e: Throwable) {
            Logger.error("AnalyticsManager", "setConsent error:", e)
        }
    }

    // -------------------------------------------------------------------------
    // Tracking
    // -------------------------------------------------------------------------

    /**
     * Track a page view.  Fire this on every SPA route change.
     *
     * @param path  The current route path, e.g. "/bedrijf/abc-123"
     */
    fun trackPageView(path: String) {
        if (!initialized) return
        try {
            val props = js("{}")
            props["\$current_url"] = kotlinx.browser.window.location.href
            props["path"] = path
            PostHogJs.capture("\$pageview", props)
        } catch (e: Throwable) {
            Logger.error("AnalyticsManager", "trackPageView error:", e)
        }
    }

    /**
     * Track a custom event.
     *
     * @param name        Event name — use a constant from [Events].
     * @param properties  Optional key-value pairs attached to this event.
     */
    fun trackEvent(name: String, properties: Map<String, Any?> = emptyMap()) {
        if (!initialized) return
        try {
            PostHogJs.capture(name, properties.toDynamic())
        } catch (e: Throwable) {
            Logger.error("AnalyticsManager", "trackEvent('$name') error:", e)
        }
    }

    /**
     * Associate all future events with a known user.
     *
     * Only call this after the user has been confirmed as authenticated **and** full analytics
     * consent has been granted. This method is a no-op when [PostHogJs.hasOptedInCapturing] is false, so
     * identity linking never happens in cookieless mode even if the caller forgets to check.
     *
     * [distinctId] should be a stable, non-PII identifier — ideally a UUID from the backend.
     * Avoid using email addresses.
     *
     * @param distinctId  Stable user identifier (e.g. user UUID)
     * @param role        Optional role label ("consumer", "company_owner", "admin")
     */
    fun identifyUser(distinctId: String, role: String? = null) {
        if (!initialized) return
        if (!PostHogJs.hasOptedInCapturing()) return
        try {
            val personProps = js("{}")
            if (!role.isNullOrBlank()) personProps["role"] = role
            PostHogJs.identify(distinctId, personProps)
        } catch (e: Throwable) {
            Logger.error("AnalyticsManager", "identifyUser error:", e)
        }
    }

    /**
     * Reset the PostHog session.  MUST be called on logout so the next user
     * on the same browser gets a fresh anonymous identity — failing to do this
     * corrupts person profiles in the PostHog dashboard.
     */
    fun resetSession() {
        if (!initialized) return
        try {
            PostHogJs.reset()
        } catch (e: Throwable) {
            Logger.error("AnalyticsManager", "resetSession error:", e)
        }
    }

    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------

    /** Convert a Kotlin Map to a plain JS object suitable for PostHog properties. */
    private fun Map<String, Any?>.toDynamic(): dynamic {
        val obj = js("{}")
        forEach { (key, value) ->
            if (value != null) obj[key] = value
        }
        return obj
    }

    /**
     * Returns a PostHog `before_send` hook that strips any remaining PII that could
     * accidentally slip through (email-shaped strings, phone numbers).
     * This is a defence-in-depth measure on top of `mask_all_inputs`.
     *
     * `before_send` receives the full event object and must return it (or null to drop the event).
     * We mutate `event.properties` in-place and always return the event so no events are dropped.
     *
     * Patterns matched:
     * - Email addresses: user@example.com
     * - International phone numbers: +32 9 123 45 67, +1 (555) 123-4567, etc.
     * - Belgian/local phone numbers: 09 123 45 67, 0032 9 123 45 67, etc.
     *
     * Patterns explicitly NOT matched (safe to send):
     * - UUIDs: 550e8400-e29b-41d4-a716-446655440000
     */
    private fun beforeSend(): dynamic {
        return js(
            """(function(event) {
                var properties = event.properties;
                if (!properties) return event;

                // Email pattern: user@example.com
                var emailPattern = /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/;
                
                // Phone pattern: matches international (+32, +1, etc.) and local/national formats.
                // Two branches:
                //   1. International: +32 9 123 45 67, +1 (555) 123-4567, 0032 9 123 45 67
                //      — country code followed by subscriber number (no leading 0 required after code)
                //   2. Local/national: 09 123 45 67, 0032 9 123 45 67
                //      — starts with leading 0 + area code
                // Requires at least 7 significant digits to avoid false-positives on IPs, ports, etc.
                var phonePattern = /(?:^|[\s,;'"\(])(?:(?:(?:\+|00)[1-9][0-9]{0,2}[\s\-\.]?)(?:0?[1-9][0-9]{1,3}[\s\-\.]?[0-9]{2,4}[\s\-\.]?[0-9]{2,4})|(?:0[1-9][0-9]{1,3}[\s\-\.]?[0-9]{2,4}[\s\-\.]?[0-9]{2,4}))(?=$|[\s,;'"\)])/;
                
                // UUID pattern: safe to include, should never be redacted
                var uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

                // Reserved PostHog URL properties — never redact, they contain paths/URLs needed for analytics
                var urlKeys = /^\${'$'}(current_url|host|pathname|referrer|referring_domain|initial_current_url|initial_referrer|initial_referring_domain)/;
                
                Object.keys(properties).forEach(function(key) {
                    // Skip PostHog reserved URL properties to avoid false positives on IPs, ports, coordinates
                    if (urlKeys.test(key)) return;

                    var val = properties[key];
                    if (typeof val === 'string') {
                        // Never redact UUIDs — they're safe and needed for tracking
                        if (uuidPattern.test(val)) {
                            return;
                        }
                        if (emailPattern.test(val) || phonePattern.test(val)) {
                            properties[key] = '[redacted]';
                        }
                    }
                });
                return event;
            })"""
        )
    }
}

